import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Plus } from "lucide-react";
import { useRef, useState, type ReactNode, type SyntheticEvent } from "react";
import type { OpsCustomerSummary, OpsPermission } from "../../shared/operations-schemas";
import type { OrganizationOverview } from "../../shared/organization-overview-schemas";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { ContentLoading } from "../components/ContentLoading";
import { Field, FieldError, FieldLabel } from "../components/ui/field";
import { Input } from "../components/ui/input";
import { CellTitle, RowTable, type RowColumn } from "../components/ui/row-table";
import { Sheet, SheetBody, SheetField, SheetFooter, SheetForm } from "../components/ui/sheet";
import { documentDate } from "../knowledge/customer-document-format";
import { createCustomerPerson, getOrganization } from "./api";
import { peopleRows, ROLE_HINTS, type PersonRow } from "./customer-people-rows";
import { formText } from "./customer-page-format";
import { CustomerWorkspaceAddress } from "./CustomerWorkspaceAddress";
import { OperationsLink } from "./OperationsLink";
import type { OperationsNavigate } from "./navigation";
import { DeploymentsSection, SupportHistorySection } from "./OrganizationSections";
import { customerPageKeys, setPrimaryContact } from "./tool-hubs-api";
import { usePrimaryContact } from "./use-primary-contact";

type Props = {
  customer: OpsCustomerSummary;
  permissions: ReadonlySet<OpsPermission>;
  navigate: OperationsNavigate;
};

const COLUMNS: RowColumn<PersonRow>[] = [
  {
    key: "person",
    header: "Person",
    cell: (row) => (
      <CellTitle
        title={
          <span className="flex flex-wrap items-center gap-2">
            {row.name}
            {row.primary ? <Badge>Primary contact</Badge> : null}
          </span>
        }
        detail={row.email ?? row.roleDetail ?? "No email recorded"}
      />
    ),
  },
  {
    key: "role",
    header: "Role",
    cell: (row) => (
      <CellTitle title={<span className="font-normal">{row.role}</span>} detail={row.roleDetail} />
    ),
  },
  {
    key: "tools",
    header: "Tools",
    className: "hidden md:table-cell text-muted-foreground",
    cell: (row) => row.tools,
  },
  {
    key: "status",
    header: "Status",
    cell: (row) => <Badge variant={row.status.variant}>{row.status.label}</Badge>,
  },
];

function usePeople(customer: OpsCustomerSummary, permissions: ReadonlySet<OpsPermission>) {
  const ready = customer.provisioningStatus === "ready";
  const canContacts = ready && permissions.has("context.read");
  const canOrganization = permissions.has("organization.read");
  const contacts = usePrimaryContact(customer.id, canContacts);
  const organization = useQuery({
    queryKey: ["ops", "customers", "organization", customer.slug],
    queryFn: () => getOrganization(customer.slug),
    enabled: canOrganization,
  });
  const overview = organization.data?.organization;
  return {
    canWrite: ready && permissions.has("context.write"),
    overview,
    loading: contacts.loading || (canOrganization && organization.isPending),
    error: organization.error ?? contacts.context.error,
    rows: peopleRows({
      contacts: contacts.people,
      members: overview ? overview.members : [],
      invitations: overview ? overview.pendingInvitations : [],
      primaryId: contacts.primary ? contacts.primary.id : null,
    }),
  };
}

export function CustomerPeople({ customer, permissions, navigate }: Props) {
  const people = usePeople(customer, permissions);
  const [selected, setSelected] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  return (
    <section aria-labelledby="customer-people-title" className="space-y-6">
      <PeopleIntro
        customer={customer}
        canWrite={people.canWrite}
        onAdd={() => {
          setAdding(true);
        }}
      />
      {people.loading ? <ContentLoading label="Loading people" /> : null}
      {people.error ? <FieldError>{people.error.message}</FieldError> : null}
      {!people.loading ? (
        <RowTable
          label="People"
          columns={COLUMNS}
          rows={people.rows}
          rowKey={(row) => row.key}
          rowLabel={(row) => row.name}
          selectedKey={selected}
          onOpen={(row) => {
            setSelected(row.key);
          }}
          empty="No people recorded yet."
        />
      ) : null}
      {people.overview ? (
        <AccessSettings
          overview={people.overview}
          customer={customer}
          permissions={permissions}
          navigate={navigate}
        />
      ) : null}
      <PersonSheet
        row={people.rows.find((row) => row.key === selected) ?? null}
        organizationId={customer.id}
        canWrite={people.canWrite}
        onClose={() => {
          setSelected(null);
        }}
      />
      <AddContactSheet
        open={adding}
        organizationId={customer.id}
        customerName={customer.name}
        onClose={() => {
          setAdding(false);
        }}
      />
    </section>
  );
}

function PeopleIntro({
  customer,
  canWrite,
  onAdd,
}: {
  customer: OpsCustomerSummary;
  canWrite: boolean;
  onAdd: () => void;
}) {
  return (
    <div className="flex flex-wrap items-end justify-between gap-4">
      <div>
        <h2 id="customer-people-title" className="text-xl font-bold">
          People
        </h2>
        <p className="mt-1 max-w-2xl text-muted-foreground">
          Everyone we work with at {customer.name}, and who can sign in to{" "}
          {new URL(customer.workspaceUrl).hostname}. Every customer has one primary contact.
        </p>
      </div>
      {canWrite ? (
        <Button onClick={onAdd}>
          <Plus /> Add contact
        </Button>
      ) : null}
    </div>
  );
}

function useMakePrimary(organizationId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async (row: PersonRow) => {
      const personId =
        row.contact?.id ??
        (
          await createCustomerPerson(organizationId, {
            displayName: row.name,
            email: row.email,
            title: null,
            phone: null,
            notes: null,
            status: "active",
          })
        ).record.id;
      return setPrimaryContact(organizationId, personId);
    },
    onSuccess: async () => {
      await Promise.all([
        client.invalidateQueries({ queryKey: customerPageKeys.primaryContact(organizationId) }),
        client.invalidateQueries({ queryKey: customerPageKeys.context(organizationId) }),
      ]);
    },
  });
}

function PersonSheet({
  row,
  organizationId,
  canWrite,
  onClose,
}: {
  row: PersonRow | null;
  organizationId: string;
  canWrite: boolean;
  onClose: () => void;
}) {
  const makePrimary = useMakePrimary(organizationId);
  const closeRef = useRef<HTMLButtonElement>(null);
  return (
    <Sheet open={row !== null} onClose={onClose} kicker="Person" title={row?.name ?? ""}>
      <SheetBody>
        {row ? <PersonDetails row={row} /> : null}
        {makePrimary.error ? <FieldError>{makePrimary.error.message}</FieldError> : null}
      </SheetBody>
      <SheetFooter>
        <Button ref={closeRef} variant="secondary" onClick={onClose}>
          Done
        </Button>
        {row && canWrite && !row.primary ? (
          <Button
            disabled={makePrimary.isPending}
            onClick={() => {
              makePrimary.mutate(row, { onSuccess: () => closeRef.current?.focus() });
            }}
          >
            Make primary contact
          </Button>
        ) : null}
      </SheetFooter>
    </Sheet>
  );
}

function workspaceRole(row: PersonRow) {
  if (row.member) return `${row.role} · ${ROLE_HINTS[row.member.role]}`;
  if (row.invitation) {
    const expires = documentDate(
      new Date(row.invitation.expiresAt * 1000).toISOString().slice(0, 10),
    );
    return `Invited as ${row.role.toLowerCase()} · expires ${expires}`;
  }
  return "Doesn’t sign in. We keep their details for our records.";
}

function PersonDetails({ row }: { row: PersonRow }) {
  const contact = row.contact;
  return (
    <>
      <SheetField label="Email">{row.email ?? "Not recorded"}</SheetField>
      {contact?.phone ? <SheetField label="Phone">{contact.phone}</SheetField> : null}
      {contact?.title ? <SheetField label="Job title">{contact.title}</SheetField> : null}
      <SheetField label="Role in the workspace">{workspaceRole(row)}</SheetField>
      {row.member ? <SheetField label="Tools">Can open {row.tools}</SheetField> : null}
      <SheetField label="Primary contact">
        {row.primary
          ? "Yes · receives proposals and invoices, and is who agents contact first"
          : "No"}
      </SheetField>
      {contact?.notes ? <SheetField label="Notes">{contact.notes}</SheetField> : null}
      {row.member ? (
        <p className="text-sm text-muted-foreground">
          The customer’s administrators change roles and tool access in their own workspace.
        </p>
      ) : null}
    </>
  );
}

function AddContactSheet({
  open,
  organizationId,
  customerName,
  onClose,
}: {
  open: boolean;
  organizationId: string;
  customerName: string;
  onClose: () => void;
}) {
  const client = useQueryClient();
  const add = useMutation({
    mutationFn: async (input: { form: FormData }) => {
      const text = (name: string) => formText(input.form, name) || null;
      const created = await createCustomerPerson(organizationId, {
        displayName: text("displayName") ?? "",
        title: text("title"),
        email: text("email"),
        phone: text("phone"),
        notes: null,
        status: "active",
      });
      if (input.form.get("primary") === "on")
        await setPrimaryContact(organizationId, created.record.id);
    },
    onSuccess: async () => {
      await Promise.all([
        client.invalidateQueries({ queryKey: customerPageKeys.context(organizationId) }),
        client.invalidateQueries({ queryKey: customerPageKeys.primaryContact(organizationId) }),
      ]);
      onClose();
    },
  });
  const submit = (event: SyntheticEvent<HTMLFormElement>) => {
    event.preventDefault();
    add.mutate({ form: new FormData(event.currentTarget) });
  };
  return (
    <Sheet open={open} onClose={onClose} title="Add contact" kicker={customerName}>
      <SheetForm onSubmit={submit} aria-label="New contact">
        <SheetBody>
          <Field>
            <FieldLabel htmlFor="contact-name">Name</FieldLabel>
            <Input id="contact-name" name="displayName" required maxLength={160} />
          </Field>
          <Field>
            <FieldLabel htmlFor="contact-title">Job title (optional)</FieldLabel>
            <Input id="contact-title" name="title" maxLength={160} />
          </Field>
          <Field>
            <FieldLabel htmlFor="contact-email">Email (optional)</FieldLabel>
            <Input id="contact-email" name="email" type="email" />
          </Field>
          <Field>
            <FieldLabel htmlFor="contact-phone">Phone (optional)</FieldLabel>
            <Input id="contact-phone" name="phone" maxLength={80} />
          </Field>
          <label className="flex min-h-11 items-center gap-3 font-semibold">
            <input type="checkbox" name="primary" className="size-5" /> Make this the primary
            contact
          </label>
          {add.error ? <FieldError>{add.error.message}</FieldError> : null}
        </SheetBody>
        <SheetFooter>
          <Button type="button" variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" disabled={add.isPending}>
            {add.isPending ? "Adding…" : "Add contact"}
          </Button>
        </SheetFooter>
      </SheetForm>
    </Sheet>
  );
}

const SIGN_IN = { emailPassword: "Password", googleSso: "Google", samlSso: "Single sign-on" };

function signInText(account: OrganizationOverview["account"]) {
  const methods = (Object.keys(SIGN_IN) as (keyof typeof SIGN_IN)[])
    .filter((method) => account.signInPolicy[method])
    .map((method) => SIGN_IN[method]);
  return methods.length ? methods.join(", ") : "Nobody can sign in yet";
}

function AccessSettings({
  overview,
  customer,
  permissions,
  navigate,
}: {
  overview: OrganizationOverview;
  customer: OpsCustomerSummary;
  permissions: ReadonlySet<OpsPermission>;
  navigate: OperationsNavigate;
}) {
  const [address, setAddress] = useState(false);
  const account = overview.account;
  const primaryHost = new URL(customer.workspaceUrl).hostname;
  return (
    <section aria-labelledby="access-settings-title" className="space-y-3">
      <h3 id="access-settings-title" className="text-xl font-bold">
        Access settings
      </h3>
      <dl className="m-0 divide-y divide-muted rounded-xl bg-card shadow-card">
        <SettingRow name="Sign-in" value={signInText(account)} />
        <SettingRow
          name="Workspace address"
          value={`${primaryHost}${account.signInPolicy.requireWorkspaceHost ? " · sign-in only from this address" : ""}`}
          action={
            permissions.has("customer.address.manage") ? (
              <Button
                variant="link"
                className="min-h-0 p-0"
                onClick={() => {
                  setAddress(true);
                }}
              >
                Change
              </Button>
            ) : null
          }
        />
        <SettingRow
          name="Support access"
          value={`We ask ${customer.name} each time we need to work inside their tools`}
          action={
            <OperationsLink
              className="font-semibold text-link"
              href={`/customers/${customer.slug}/support`}
              navigate={navigate}
            >
              Open
            </OperationsLink>
          }
        />
        <SettingRow
          name="Account"
          value={`${account.status.charAt(0).toUpperCase()}${account.status.slice(1)} · customer since ${documentDate(new Date(account.createdAt * 1000).toISOString().slice(0, 10))}`}
        />
      </dl>
      <TechnicalHistory overview={overview} />
      <Sheet
        open={address}
        onClose={() => {
          setAddress(false);
        }}
        title="Workspace address"
        kicker={customer.name}
      >
        <CustomerWorkspaceAddress customer={customer} />
      </Sheet>
    </section>
  );
}

function SettingRow({ name, value, action }: { name: string; value: string; action?: ReactNode }) {
  return (
    <div className="grid gap-x-6 gap-y-1 px-6 py-4 sm:grid-cols-[12rem_minmax(0,1fr)_auto] sm:items-center">
      <dt className="font-semibold">{name}</dt>
      <dd className="m-0 text-muted-foreground">{value}</dd>
      <dd className="m-0">{action}</dd>
    </div>
  );
}

function TechnicalHistory({ overview }: { overview: OrganizationOverview }) {
  return (
    <details className="rounded-xl bg-card px-6 py-2 shadow-card">
      <summary className="flex min-h-11 cursor-pointer items-center font-semibold text-link">
        Deployments and support history
      </summary>
      <div className="space-y-6 py-4">
        <DeploymentsSection tools={overview.tools} />
        <SupportHistorySection
          events={overview.supportHistory}
          total={overview.supportHistoryTotal}
        />
      </div>
    </details>
  );
}
